import {
  useCallback,
  useState,
  type FocusEvent as ReactFocusEvent,
} from 'react';

import * as styles from '../styles/focusRing.sty.js';
import { modalityStore } from '../utils/modality.js';

/** @public */
export interface UseFocusRingProps {
  /**
   * A minimal focus outline is available for smaller content elements like links. This variant is applied when set to true.
   * @defaultValue false
   */
  isMinimal?: boolean;

  /**
   * Whether or not the modality should be ignored, so that the focus ring is also present if users clicks the element (not just by tabbing to it).
   * @defaultValue false
   */
  ignoreModality?: boolean;

  /**
   * The styling color of the element.
   * @defaultValue neutral
   */
  color?: 'neutral' | 'primary' | 'success' | 'warning' | 'critical';

  /**
   * The styling color of the element.
   * @defaultValue neutral
   * @deprecated - `variant` is deprecated, please use `color` instead.
   */
  variant?: 'neutral' | 'primary' | 'success' | 'warning' | 'critical';

  /**
   * Apply the focus ring styling if an embedded element gains the focus
   */
  focusWithin?: boolean;

  /**
   * Whether the element is disabled. If disabled changes to true, the focus will be removed.
   */
  disabled?: boolean;
}

/** @public */
export type FocusRingProps = {
  /** The focus styles that depend on modality. */
  focusClassName: string;
  /** Focus handlers used to manage the focus state. Must be applied to the element that receives the focus.*/
  focusProps: {
    onFocus: (e: ReactFocusEvent | FocusEvent) => void;
    onBlur: (e: ReactFocusEvent | FocusEvent) => void;
  };
  /** Whether or not the element got focus via keyboard. */
  isFocusVisible: boolean;
};

/**
 * Custom hook used to determine focus styling depending on modality.
 * Returns the css classes (vanilla) that can be applied to the component in order to show the correct focus outline.
 * @public
 */
export function useFocusRing(props: UseFocusRingProps = {}): FocusRingProps {
  const {
    color,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    variant,
    ignoreModality = false,
    isMinimal = false,
    focusWithin = false,
    disabled = false,
  } = props;
  const [isFocused, setIsFocused] = useState(false);

  const modality = modalityStore.getState();

  const usedColor = color ?? variant ?? 'neutral';

  const onBlur = useCallback((event: ReactFocusEvent | FocusEvent) => {
    // trigger only when the self element is unfocused (stopPropagation)
    if (event.currentTarget === event.target) {
      setIsFocused(false);
    }
  }, []);

  const onFocus = useCallback((event: ReactFocusEvent | FocusEvent) => {
    // trigger only when the self element is focused (stopPropagation)
    if (event.currentTarget === event.target) {
      setIsFocused(true);
    }
  }, []);

  const focusProps = {
    onFocus,
    onBlur,
  };

  if (
    (ignoreModality || modality === 'keyboard') &&
    (isFocused || focusWithin) &&
    !disabled
  ) {
    if (isMinimal) {
      return {
        focusClassName: focusWithin
          ? styles.focusRingFocusWithin({
              minimal: true,
              color: usedColor,
            })
          : styles.focusRing({ minimal: true, color: usedColor }),
        isFocusVisible: !focusWithin,
        focusProps,
      };
    }

    return {
      focusClassName: focusWithin
        ? styles.focusRingFocusWithin({ minimal: false, color: usedColor })
        : styles.focusRing({ minimal: false, color: usedColor }),
      isFocusVisible: !focusWithin,
      focusProps,
    };
  }

  return {
    focusClassName: focusWithin
      ? styles.focusRingFocusWithin()
      : styles.focusRing(),
    isFocusVisible: false,
    focusProps,
  };
}
